import { useEffect } from "react";
import { editorStore } from "../store/editorStore";
import { renderStore } from "../store/renderStore";
import type { ExportFormatOption } from "../utils/exportFormats";

interface UseExportPreviewSnapshotParams {
  selectedFormatId: string;
  selectedFormat: Pick<ExportFormatOption, "orientation" | "format">;
  drawablePixelSize: { width: number; height: number };
  setIsInitializing: (value: boolean) => void;
}

/**
 * Captures the WebGL preview only when the paper format/size changes — not on every
 * pan/zoom (that would re-render the full canvas each time).
 * Always captures the full scene; CSS zoom/pan handles visual framing in the preview.
 * The exported file uses `captureMetrics.captureBox` for final rendering.
 */
export const useExportPreviewSnapshot = ({
  selectedFormatId,
  selectedFormat,
  drawablePixelSize,
  setIsInitializing,
}: UseExportPreviewSnapshotParams) => {
  const formatKey = `${selectedFormatId}-${drawablePixelSize.width}-${drawablePixelSize.height}`;

  useEffect(() => {
    let cancelled = false;

    setIsInitializing(true);

    const run = async () => {
      const cam = editorStore.camera;
      if (!cam) {
        if (!cancelled) {
          setIsInitializing(false);
        }
        return;
      }

      try {
        // Always capture the full scene for the preview snapshot.
        // CSS zoom/pan handles visual framing. Passing the captureBox here
        // would bake in a stale zoom when the format changes, causing symbols
        // to appear at inconsistent sizes between portrait and landscape.
        const target = {
          orientation: selectedFormat.orientation,
          format: selectedFormat.format,
          width: drawablePixelSize.width,
          height: drawablePixelSize.height,
        };

        if (editorStore.view3D) {
          await renderStore.capture3DSnapshot("preview", target, cam);
        } else {
          await renderStore.captureSnapshot(
            "preview",
            target,
            undefined,
            cam,
          );
        }
      } finally {
        if (!cancelled) {
          setIsInitializing(false);
        }
      }
    };

    void run();

    return () => {
      cancelled = true;
    };
  }, [
    drawablePixelSize.height,
    drawablePixelSize.width,
    formatKey,
    editorStore.view3D,
    selectedFormat.format,
    selectedFormat.orientation,
    setIsInitializing,
  ]);
};

/** @deprecated Use `useExportPreviewSnapshot` instead. */
export const useDebouncedExportPreviewCapture = useExportPreviewSnapshot;
